'use client';

import NavItem from '@/app-shell/layout/Navigation/sidebar/nav-item';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  showLess: {
    defaultMessage: 'Less',
    id: 'Metq/rEQ+G',
    description: 'Sidebar toggle label that collapses the grouped nav items',
  },
  showMore: {
    defaultMessage: 'More',
    id: 'ecfvCtHwQV',
    description: 'Sidebar toggle label that expands the grouped nav items',
  },
});

interface ExpandableNavItemGroupProps {
  /** Children nav items to show when expanded */
  children: React.ReactNode;
  /** Whether the item should be open by default */
  defaultOpen?: boolean;
  /** When false, renders children in a fragment without the expandable UI */
  enabled?: boolean;
  /** Whether this item is currently active */
  isActive?: boolean;
  /** Test ID for the nav item */
  testId?: string;
}

/**
 * ExpandableNavItem is a collapsible navigation item that can show/hide child nav items.
 * e.g. grouping navigation items like Tasks and Templates under a "More" menu.
 */
export const ExpandableNavItemGroup = ({
  children,
  defaultOpen = false,
  enabled = true,
  isActive = false,
  testId,
}: ExpandableNavItemGroupProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen || isActive);
  const intl = useIntl();

  if (!enabled) {
    return <>{children}</>;
  }

  const label = intl.formatMessage(isOpen ? messages.showLess : messages.showMore);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="group/collapsible w-full"
      data-testid={testId}
    >
      <CollapsibleContent className="pb-2">{children}</CollapsibleContent>
      <CollapsibleTrigger asChild>
        <NavItem isActive={isActive} label={label} icon={<MoreHorizontal />} />
      </CollapsibleTrigger>
    </Collapsible>
  );
};
